import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Grid,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";

import {
  TimelineOppositeContent,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import {
  ArrowBack,
  Download,
  History,
  Description,
  Event,
} from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import Cookies from "js-cookie";
import dayjs from "dayjs";

// Import components
import MainLayout from "../../components/MainLayout";
import Breadcrumb from "../../components/Breadcrumb";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import LoadingScreen from "../LoadingPage";

// Import services
import contractService from "../../services/contract";
import queryDefault from "../../utils/queryDefault";
import useNotification from "../../hooks/useNotification";

// Function to get status chip based on contract status
const getStatusChip = (status: string) => {
  let color: "success" | "warning" | "info" | "primary" | "error" | "default" =
    "default";
  switch (status) {
    case "active":
      color = "success";
      break;
    case "employee_review":
      color = "warning";
      break;
    case "hr_review":
      color = "info";
      break;
    case "completed":
      color = "primary";
      break;
    case "cancelled":
    case "rejected":
      color = "error";
      break;
  }

  return <Chip label={status} color={color} size="small" />;
};

// Function to get icon based on action type
const getActionIcon = (action: string) => {
  switch (action) {
    case 'document_downloaded':
      return <Download />;
    case 'document_created':
      return <Description />;
    case 'contract_created':
      return <Event />;
    case 'status_changed':
      return <Event />;
    default:
      return <Description />;
  }
};

// Function to get color based on action type
const getActionColor = (action: string): "success" | "warning" | "info" | "primary" | "error" | "secondary" | "grey" | "inherit" => {
  switch (action) {
    case 'document_downloaded':
      return "grey";
    case 'document_created':
      return "info";
    case 'contract_created':
      return "primary";
    case 'status_changed':
      return "success";
    default:
      return "grey";
  }
};

// Function to get action title
const getActionTitle = (history: any) => {
  switch (history.action) {
    case 'document_downloaded':
      return "Document Downloaded";
    case 'document_created':
      return "Document Created";
    case 'contract_created':
      return "Contract Created";
    case 'status_changed':
      return `Status Changed to: ${history.new_status}`;
    default:
      return history.action.replace('_', ' ');
  }
};

export default function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notification = useNotification();
  const user = JSON.parse(Cookies.get("USER") || "{}");

  // Fetch contract details
  const { data: contractDetail, isLoading } = useQuery(
    ["contractDetail", id],
    () => contractService.viewContractDetail(Number(id)),
    {
      ...queryDefault,
      enabled: !!id,
    }
  );

  // Fetch contract history
  const { data: contractHistory, isLoading: historyLoading } = useQuery(
    ["contractHistory", id],
    () => contractService.getContractHistory(Number(id)),
    {
      ...queryDefault,
      enabled: !!id,
    }
  );

  // Download contract document mutation
  const downloadContract = useMutation(contractService.downloadContract, {
    onSuccess: () => {
      notification.onOpen({
        message: "Contract document was downloaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to download contract",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  // Download history document mutation
  const downloadHistoryDocument = useMutation(
    contractService.downloadHistoryDocument,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "History document was downloaded successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to download history document",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const handleDownload = () => {
    if (id) {
      downloadContract.mutate(Number(id));
    }
  };

  const handleDownloadHistory = (documentId: number, documentPath: string) => {
    downloadHistoryDocument.mutate({ documentId, documentPath });
  };

  // Display loading screen while data is being fetched
  if (isLoading || historyLoading) {
    return <LoadingScreen />;
  }

  const contract = contractDetail?.contract;

  return (
    <MainLayout>
      <Helmet>
        <title>Contract Details</title>
      </Helmet>

      <Breadcrumb
        label="Contract Details"
        breadcrumbs={[
          { label: "Dashboard", href: "/app" },
          { label: "Contracts", href: "/app/contract" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1,2]}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(`/app/${user.role_id === 1 ? "contract" : "employee-dashboard"}`)}
              >
                Back to List
              </Button>
              <Typography variant="h5">Contract Details</Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownload}
              disabled={downloadContract.isLoading}
            >
              Download Current Contract
            </Button>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {contract && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="body1">
                      {contract.employee_name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Position
                    </Typography>
                    <Typography variant="body1">
                      {contract.position || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1">
                      {contract.department || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contract Type
                    </Typography>
                    <Typography variant="body1">
                      {contract.contract_type || "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    {getStatusChip(contract.status)}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contract Start Date
                    </Typography>
                    <Typography variant="body1">
                      {contract.contract_start
                        ? dayjs(contract.contract_start).format("DD/MM/YYYY")
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contract End Date
                    </Typography>
                    <Typography variant="body1">
                      {contract.contract_end
                        ? dayjs(contract.contract_end).format("DD/MM/YYYY")
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created Date
                    </Typography>
                    <Typography variant="body1">
                      {contract.created_at
                        ? dayjs(contract.created_time).format("DD/MM/YYYY HH:mm")
                        : "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {contract.rejection_reason && (
                <Grid item xs={12}>
                  <Card sx={{ bgcolor: "error.light", mt: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Rejection Reason:
                      </Typography>
                      <Typography variant="body1">
                        {contract.rejection_reason}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </Paper>

        {/* Contract History Section */}
        <Card elevation={3}>
          <CardHeader
            title="Contract History"
            avatar={<History color="primary" />}
          />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            {contractHistory?.history && contractHistory.history.length > 0 ? (
              <Timeline position="alternate">
                {contractHistory.history.map((history: any, index: number) => (
                  <TimelineItem key={history.id}>
                    <TimelineOppositeContent color="text.secondary">
                      {dayjs(history.performed_at).format("DD MMM YYYY HH:mm")}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={getActionColor(history.action)}>
                        {getActionIcon(history.action)}
                      </TimelineDot>
                      {index < contractHistory.history.length - 1 && (
                        <TimelineConnector />
                      )}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Paper elevation={2} sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body1" fontWeight="bold">
                            {getActionTitle(history)}
                          </Typography>

                          {history.document_path && (
                            <Tooltip title="Download document version">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadHistory(history.id, history.document_path)}
                                disabled={downloadHistoryDocument.isLoading}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>

                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {history.reason || "No description provided"}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          By: {history.performed_by_name} ({history.performed_by_email})
                        </Typography>

                        {history.previous_status && history.new_status && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mt: 1 }}
                          >
                            <Typography variant="caption">From:</Typography>
                            {getStatusChip(history.previous_status)}
                            <Typography variant="caption">To:</Typography>
                            {getStatusChip(history.new_status)}
                          </Stack>
                        )}
                      </Paper>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1">
                  No history records found for this contract.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
